import React, { useRef, useCallback, useMemo, useEffect } from 'react'
import { Checkbox, Empty, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import Highlighter from 'react-highlight-words'
import './CustomCheckboxGroup.css'

interface CheckboxOption<T extends string | number = string | number> {
  label: React.ReactNode
  /** Plain text used for filtering when label is a ReactNode */
  filterLabel?: string
  value: T
}

interface CustomCheckboxGroupProps<T extends string | number = string | number> {
  options: CheckboxOption<T>[]
  value?: T[]
  onChange?: (values: T[]) => void
  /** External search text for filtering and highlighting */
  searchText?: string
  /** Render extra content to the right of each checkbox row */
  renderExtra?: (value: T, checked: boolean) => React.ReactNode
  /** Value of the currently active/focused row */
  activeValue?: T | null
  /** Custom styles applied to each row */
  rowStyle?: React.CSSProperties
  height?: number
  style?: React.CSSProperties
}

/** Checkbox group with shift+click range selection, optional filtering and highlighting */
const CustomCheckboxGroup = <T extends string | number = string | number>({
  options,
  value = [],
  onChange,
  searchText = '',
  renderExtra,
  activeValue,
  rowStyle,
  height,
  style
}: CustomCheckboxGroupProps<T>) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const shiftAnchorRef = useRef<T | null>(null)
  const firstCheckedRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    firstCheckedRef.current?.scrollIntoView({ block: 'nearest' })
  }, [])

  const filteredOptions = useMemo(() => {
    if (!searchText) return options
    const lower = searchText.toLowerCase()
    return options.filter(o => {
      const text = o.filterLabel ?? (typeof o.label === 'string' ? o.label : '')
      return text.toLowerCase().includes(lower)
    })
  }, [options, searchText])

  const handleClick = useCallback(
    (optionValue: T, e: React.MouseEvent) => {
      const isChecked = value.includes(optionValue)
      const willCheck = !isChecked

      if (e.shiftKey && shiftAnchorRef.current !== null) {
        const keys = filteredOptions.map(o => o.value)
        const anchorIdx = keys.indexOf(shiftAnchorRef.current)
        const currentIdx = keys.indexOf(optionValue)

        if (anchorIdx !== -1 && currentIdx !== -1) {
          const start = Math.min(anchorIdx, currentIdx)
          const end = Math.max(anchorIdx, currentIdx)
          const rangeKeys = keys.slice(start, end + 1)

          const newValue = willCheck
            ? Array.from(new Set([...value, ...rangeKeys]))
            : value.filter(v => !rangeKeys.includes(v))

          onChange?.(newValue)
          shiftAnchorRef.current = optionValue
          return
        }
      }

      shiftAnchorRef.current = optionValue

      const newValue = willCheck ? [...value, optionValue] : value.filter(v => v !== optionValue)

      onChange?.(newValue)
    },
    [filteredOptions, value, onChange]
  )

  const renderLabel = (option: CheckboxOption<T>) => {
    if (!searchText || typeof option.label !== 'string') return option.label
    return <Highlighter searchWords={[searchText]} autoEscape textToHighlight={option.label} />
  }

  if (filteredOptions.length === 0) {
    return <Empty description={t('global:no-results')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
  }

  return (
    <div
      className="custom-checkbox-group"
      style={
        {
          display: 'flex',
          flexDirection: 'column',
          background: token.colorBgContainer,
          borderRadius: token.borderRadius,
          padding: 4,
          '--checkbox-group-hover-bg': token.controlItemBgHover,
          '--checkbox-group-active-bg': token.controlItemBgActive,
          ...(height && { maxHeight: height, overflowY: 'auto' as const }),
          ...style
        } as React.CSSProperties
      }
    >
      {
        filteredOptions.reduce<{ elements: React.ReactNode[]; foundFirst: boolean }>(
          (acc, option) => {
            const checked = value.includes(option.value)
            const isFirstChecked = checked && !acc.foundFirst
            if (isFirstChecked) acc.foundFirst = true
            acc.elements.push(
              <div
                key={String(option.value)}
                ref={isFirstChecked ? firstCheckedRef : undefined}
                className={`custom-checkbox-group-row${activeValue === option.value ? ' active' : ''}`}
                style={{ display: 'flex', alignItems: 'center', ...rowStyle }}
              >
                <Checkbox
                  checked={checked}
                  onClick={e => handleClick(option.value, e)}
                  style={{ flex: 1, padding: '2px 4px', borderRadius: token.borderRadiusSM }}
                >
                  {renderLabel(option)}
                </Checkbox>
                {renderExtra?.(option.value, checked)}
              </div>
            )
            return acc
          },
          { elements: [], foundFirst: false }
        ).elements
      }
    </div>
  )
}

export default CustomCheckboxGroup
